import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [testMode, setTestMode] = useState(null); // For super admin to test as different roles

  // iter 294.4 — Tenant Scope Everywhere. Super admins have no
  // company_id of their own. When they pick a tenant here we stash the
  // choice in localStorage and inject `X-Company-Id: <tenant>` on every
  // subsequent request. Backend's get_current_user() reads that header
  // and mutates user["company_id"], so every existing per-tenant
  // endpoint (broker, accounting, moving, terminals, …) becomes
  // super-admin-usable without per-endpoint changes.
  const [superAdminActingAs, _setSuperAdminActingAs] = useState(() => {
    try { return localStorage.getItem('super_admin_acting_as') || ''; } catch { return ''; }
  });
  const [superAdminActingAsName, _setSuperAdminActingAsName] = useState(() => {
    try { return localStorage.getItem('super_admin_acting_as_name') || ''; } catch { return ''; }
  });
  const superAdminActingAsRef = React.useRef(superAdminActingAs);
  const setSuperAdminActingAs = useCallback((companyId, companyName = '') => {
    superAdminActingAsRef.current = companyId || '';
    _setSuperAdminActingAs(companyId || '');
    _setSuperAdminActingAsName(companyName || '');
    try {
      if (companyId) {
        localStorage.setItem('super_admin_acting_as', companyId);
        if (companyName) localStorage.setItem('super_admin_acting_as_name', companyName);
        else localStorage.removeItem('super_admin_acting_as_name');
      } else {
        localStorage.removeItem('super_admin_acting_as');
        localStorage.removeItem('super_admin_acting_as_name');
      }
    } catch { /* ignore */ }
  }, []);

  // iter 264 — JWT storage hardening.
  // Instead of relying on a long-lived 12h localStorage token, we now:
  //   1. Keep the access token in localStorage (for backwards-compat with the
  //      ~15 places across the app that read it directly) BUT
  //   2. On 401 with an "access token expired" hint, silently call
  //      /auth/refresh which reads the httpOnly refresh cookie set at login
  //      and returns a fresh access token.
  //   3. Rotate the cookie on every refresh (single-use semantics).
  // This drops the XSS blast-radius from 12h → the access-token TTL while
  // keeping the current-session UX intact.
  const refreshInFlightRef = React.useRef(null);
  const trySilentRefresh = React.useCallback(async () => {
    // Coalesce concurrent refreshes so we don't hammer the backend when
    // multiple parallel requests all get 401 at once.
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    refreshInFlightRef.current = (async () => {
      try {
        const r = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const newToken = r?.data?.access_token;
        if (newToken) {
          localStorage.setItem('token', newToken);
          setToken(newToken);
          if (r.data.user) setUser((prev) => prev ?? r.data.user);
          return newToken;
        }
        return null;
      } catch (e) {
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();
    return refreshInFlightRef.current;
  }, []);

  // Create axios instance with useMemo to avoid recreation on each render
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      // Send the httpOnly refresh cookie with same-origin requests. Preview
      // domain and production share host with the frontend, so this stays
      // same-origin in practice.
      withCredentials: true,
    });
    
    // Add request interceptor to include token
    instance.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        // iter 294.4 — inject the super-admin's chosen tenant on every
        // request. Backend only honours this when the JWT role is
        // super_admin, so a normal user forging this header does nothing.
        const acting = superAdminActingAsRef.current;
        if (acting) {
          const url = String(config.url || '');
          // Skip super-admin management endpoints and the auth bootstrap
          // — those must always operate at the super-admin scope.
          const skip = url.includes('/super-admin/') || url.includes('/auth/');
          if (!skip) {
            config.headers['X-Company-Id'] = acting;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to normalize Pydantic validation errors and
    // globally handle expired/invalid tokens. Any 401 outside the /auth/me
    // bootstrap path means the session is dead — clear it and bounce to /login.
    // FastAPI 422 returns detail as array of objects [{type, loc, msg, input}]
    // which crashes React if passed to toast.error() directly.
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            error.response.data.detail = detail
              .map(d => d.msg || d.message || JSON.stringify(d))
              .join('; ');
          } else if (typeof detail === 'object') {
            error.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
          }
        }

        // iter 264 — JWT storage hardening: try silent refresh on 401 for
        // "Token has expired" BEFORE nuking the session. If the refresh cookie
        // is still valid, we get a fresh access token and can retry the
        // original request transparently. If refresh fails, fall through to
        // the session-expired branch below.
        const status = error.response?.status;
        const errDetail = String(error.response?.data?.detail || "").toLowerCase();
        const isTokenExpired = status === 401 && errDetail.includes("expired");
        const originalReq = error.config || {};
        if (isTokenExpired && !originalReq._retried && !String(originalReq.url || "").includes("/auth/refresh")) {
          originalReq._retried = true;
          const fresh = await trySilentRefresh();
          if (fresh) {
            originalReq.headers = { ...(originalReq.headers || {}), Authorization: `Bearer ${fresh}` };
            return instance.request(originalReq);
          }
        }

        // Global session-expired handling. Skip the auth bootstrap path so we
        // don't fight with checkAuth (which has its own 401 logic). We don't
        // call setUser/setToken here because a full-page redirect re-mounts
        // AuthProvider with a clean state — much safer than touching React
        // state from inside a useMemo'd interceptor.
        //
        // We treat BOTH 401 and "403 with no token present" as session-expired.
        // Some endpoints (e.g. /gate-schedules) return 403 instead of 401 when
        // the Authorization header is missing — without the 403 branch, the
        // user would stay on the page with no feedback.
        const hasTokenNow = !!localStorage.getItem('token');
        const isSessionExpiredCandidate =
          status === 401 || (status === 403 && !hasTokenNow);

        if (isSessionExpiredCandidate) {
          const url = error.config?.url || '';
          // Denylist of paths where 401 means "the external service rejected
          // this credential" — NOT "your Drayly session is dead". These
          // 401s should propagate as errors to the calling component, NOT
          // log the user out. Backend should ideally return 400/422 here
          // (and we've fixed the worst offenders in iter233), but defensive
          // denylist keeps a future regression from kicking users out.
          const thirdPartyAuthPaths = [
            '/terminal-integration/credentials/',  // test endpoint + scrapers
            '/quickbooks/',
            '/webhook/',
          ];
          const isThirdPartyAuth = thirdPartyAuthPaths.some((p) => url.includes(p));
          if (!isThirdPartyAuth && !url.includes('/auth/me') && !url.includes('/auth/login')) {
            // Always clear an expired token, even if no localStorage entry,
            // so React state stays consistent.
            const hadToken = hasTokenNow;
            localStorage.removeItem('token');
            const path = typeof window !== 'undefined' ? window.location.pathname : '';
            // Public pages stay where they are — but the marketplace IS the
            // canonical post-session-expiry landing (marketing-first), so
            // route any authenticated-only page there with a banner flag.
            const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/privacy', '/terms', '/about', '/contact', '/home', '/marketplace'];
            if (typeof window !== 'undefined' && !publicRoutes.some((p) => path.startsWith(p))) {
              // 401 always redirects (true session expiry). 403 only when no
              // token was present (likely a stale-session scenario). A real
              // 403 on an authenticated user (role mismatch) propagates and
              // the calling component can show the proper "forbidden" message.
              if (hadToken || status === 403) {
                window.location.href = '/marketplace?session=expired';
              }
            }
          }
        }

        return Promise.reject(error);
      }
    );
    
    return instance;
  }, [trySilentRefresh]);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      // iter 264 — no localStorage token, but maybe the httpOnly refresh
      // cookie is still valid (e.g. user hard-refreshed after browser
      // cleared localStorage). Try silent refresh once — if it works, we
      // have a fresh access token to use.
      const fresh = await trySilentRefresh();
      if (!fresh) {
        setLoading(false);
        return;
      }
      // fall through — trySilentRefresh populated localStorage + setToken.
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // Only clear token if it's actually invalid (401), not on network errors
      if (error.response?.status === 401) {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
      } else {
        // For network errors, keep the token and let user retry
        console.error('Auth check failed:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [api, trySilentRefresh]);

  useEffect(() => {
    // Check auth on mount, regardless of token state in React
    // The checkAuth function will read from localStorage
    checkAuth();
  }, [checkAuth]);

  const login = async (identifier, password) => {
    const response = await api.post('/auth/login', { identifier, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    // iter 294.10c — Immediately re-fetch /auth/me so the user object carries
    // company_category / company_name (populated server-side) that the login
    // payload doesn't currently include. Without this the sidebar's Moving
    // cluster stays hidden until the user manually reloads.
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
      return me.data;
    } catch {
      setUser(userData);
      return userData;
    }
  };

  const register = async (email, password, name, role = 'dispatcher', extra = {}) => {
    const response = await api.post('/auth/register', { email, password, name, role, ...extra });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
      return me.data;
    } catch {
      setUser(userData);
      return userData;
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use window.location.origin + '/dashboard' as the redirect target
    // Emergent Auth will append #session_id=xxx to this URL after Google auth
    const redirectUrl = window.location.origin + '/dashboard';
    console.log('loginWithGoogle: Initiating Google OAuth');
    console.log('loginWithGoogle: Redirect URL:', redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    try {
      console.log('processGoogleCallback: Calling backend /auth/session with session_id');
      const marketplace = sessionStorage.getItem('mkt_google') === '1';
      sessionStorage.removeItem('mkt_google');
      const response = await api.post('/auth/session', { session_id: sessionId, marketplace });
      console.log('processGoogleCallback: Backend response received');
      
      // Save token if returned (for Google OAuth)
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        setToken(response.data.access_token);
      }
      
      // Fetch the full profile (company category etc.) so landing routes use the right home.
      let userData = {
        user_id: response.data.user_id,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        picture: response.data.picture,
        created_at: response.data.created_at
      };
      try { userData = (await api.get('/auth/me')).data; } catch { /* keep basic profile */ }
      
      setUser(userData);
      setLoading(false);
      
      return userData;
    } catch (error) {
      console.error('processGoogleCallback error:', error);
      console.error('processGoogleCallback error response:', error.response?.data);
      console.error('processGoogleCallback error status:', error.response?.status);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors on logout
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  // iter 258 — Admin Impersonation. Super admin can mint a token that
  // impersonates any non-super-admin user to debug customer-reported issues.
  // The returned token replaces the current session token; we then reload the
  // app so every component re-fetches with the new identity. End-impersonation
  // swaps back to the original super-admin token.
  const startImpersonation = async (targetUserId, redirectTo = '/dashboard') => {
    const response = await api.post(`/super-admin/impersonate/${targetUserId}`);
    const { access_token } = response.data;
    // Stash original super-admin token so a network failure during /auth/me
    // doesn't permanently lock the super admin out of their own account.
    const previousToken = localStorage.getItem('token');
    if (previousToken) {
      localStorage.setItem('original_admin_token', previousToken);
    }
    localStorage.setItem('token', access_token);
    setToken(access_token);
    // Hard reload so route guards + WebSocket + every cached fetch picks up
    // the new identity. Avoids subtle stale-state bugs.
    // iter 264d.6.1 — callers can now redirect to a specific screen
    // (e.g. the audit tile jumps into /settings/terminals?editCred=<id>).
    window.location.href = redirectTo;
  };

  const endImpersonation = async () => {
    try {
      const response = await api.post('/super-admin/impersonate/end');
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.removeItem('original_admin_token');
      setToken(access_token);
      window.location.href = '/super-admin';
    } catch (e) {
      // Fallback: if the end-impersonation call fails, restore the stashed
      // original token so the super admin isn't stranded inside the
      // impersonated session. This is a last-resort safety net.
      const original = localStorage.getItem('original_admin_token');
      if (original) {
        localStorage.setItem('token', original);
        localStorage.removeItem('original_admin_token');
        setToken(original);
        window.location.href = '/super-admin';
      } else {
        throw e;
      }
    }
  };

  // Get effective role (considers test mode for super admins)
  const effectiveRole = testMode && user?.role === 'super_admin' ? testMode : user?.role;
  const effectiveUser = testMode && user?.role === 'super_admin' 
    ? { ...user, role: testMode, _actualRole: 'super_admin' }
    : user;

  // Enable/disable test mode (super admin only)
  const setTestAsRole = useCallback((role) => {
    // Check actual user role (not effective role) to allow super admin to change test mode
    if (user?.role === 'super_admin' || testMode) {
      setTestMode(role); // null to exit test mode
    }
  }, [user?.role, testMode]);

  const value = {
    user: effectiveUser,
    actualUser: user, // Always the real user
    loading,
    token,
    api,
    login,
    register,
    loginWithGoogle,
    processGoogleCallback,
    logout,
    checkAuth,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: effectiveRole === 'admin',
    isSuperAdmin: user?.role === 'super_admin', // Always check actual role
    isDispatcher: effectiveRole === 'dispatcher',
    isDriver: effectiveRole === 'driver',
    // Test mode features for super admin
    testMode,
    setTestAsRole,
    isInTestMode: !!testMode && user?.role === 'super_admin',
    exitTestMode: () => setTestMode(null),
    // iter 258 — Admin Impersonation
    startImpersonation,
    endImpersonation,
    isImpersonating: !!user?.impersonating,
    impersonationOriginalEmail: user?.original_email,
    // iter 294.4 — Tenant Scope Everywhere
    superAdminActingAs,
    superAdminActingAsName,
    setSuperAdminActingAs,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
